<template>
  <div class="mx-auto" :class="isMobile ? 'mb-4' : 'mb-5'">
    <div class="fs-20 font-weight-bold detail-title">
      {{ info.rego ? `[${info.rego}]` : '' }}
      {{ info.year }} {{ info.make.toUpperCase() }}
      {{ info.model.toUpperCase() }}
      {{ info.badge.toUpperCase() }}
      {{ info.edition.toUpperCase() }}
    </div>
    <div class="customer-detail">
      <div
        class="
          customer-detail-content
          justify-content-between
          align-items-center
          text-center
          mx-auto
        "
      >
        <div class="right-content-detail">
          <p class="title fs-16 mb-0 mx-auto font-weight-bold">
            Almost there, just a few more details!
          </p>
          <b-row class="content-detail">
            <b-col cols="12" md="6">
              <div class="position-relative">
                <float-label label="Reserve price" class="mb-0">
                  <b-input
                    v-model.trim="customerDetail.reservePrice"
                    placeholder="Enter your reserve price"
                    ref="reservePriceInput"
                    autocomplete="off"
                    required
                    type="number"
                    pattern="\d*"
                    @keypress="isNumber"
                    :class="[
                      {
                        error: $v.customerDetail.reservePrice.$error,
                      },
                      'shadow-none',
                    ]"
                    @keyup.enter="setNextInput('name')"
                    @keyup="removeFirstZero"
                  />
                  <p
                    v-if="
                      $v.customerDetail.reservePrice.$dirty &&
                      !$v.customerDetail.reservePrice.required
                    "
                    class="msg-error mb-0"
                  >
                    <i>Please enter your reserve price</i>
                  </p>
                  <p
                    v-else-if="
                      $v.customerDetail.reservePrice.$dirty &&
                      !$v.customerDetail.reservePrice.numeric
                    "
                    class="msg-error mb-0"
                  >
                    <i>Please enter only numerics!</i>
                  </p>
                  <p
                    v-else-if="
                      $v.customerDetail.reservePrice.$dirty &&
                      !$v.customerDetail.reservePrice.maxLength
                    "
                    class="msg-error mb-0"
                  >
                    <i>Please enter 1 to 20 number!</i>
                  </p>
                </float-label>
              </div>
              <p
                class="price-desc fs-8 text-right mb-2"
                :class="[
                  !$v.customerDetail.reservePrice.$error ? 'mt-n2' : 'mt-1',
                ]"
              >
                <i>Tell us the minimum amount you would be happy with!</i>
              </p>
            </b-col>
            <b-col cols="12" md="6">
              <div class="position-relative">
                <float-label label="Name">
                  <b-input
                    v-model.trim="customerDetail.customerName"
                    @blur="fullNameValidCheck"
                    placeholder="Enter your full name"
                    autocomplete="off"
                    required
                    :class="[
                      {
                        error: $v.customerDetail.customerName.$error,
                      },
                      {
                        'mt-17': isMobile,
                      },
                      'shadow-none',
                    ]"
                    @keyup.enter="setNextInput('email')"
                  />
                  <p
                    v-if="
                      $v.customerDetail.customerName.$dirty &&
                      !$v.customerDetail.customerName.required
                    "
                    class="msg-error mb-0"
                  >
                    <i>Please enter your name</i>
                  </p>
                  <p
                    v-else-if="
                      $v.customerDetail.customerName.$dirty &&
                      !$v.customerDetail.customerName.valid
                    "
                    class="msg-error mb-0"
                  >
                    <i
                      >Please enter only alphabet characters, numerics and
                      space!</i
                    >
                  </p>
                  <p
                    v-else-if="
                      $v.customerDetail.customerName.$dirty &&
                      !$v.customerDetail.customerName.maxLength
                    "
                    class="msg-error mb-0"
                  >
                    <i>Please enter 1 to 110 characters!</i>
                  </p>
                  <p
                    v-else-if="!isNotShowFullNameErr"
                    class="msg-error mb-0"
                  >
                    <i>Please enter your full name.</i>
                  </p>
                </float-label>
              </div>
            </b-col>
            <b-col cols="12" md="6">
              <div class="position-relative">
                <float-label label="Email">
                  <b-input
                    v-model.trim="customerDetail.customerEmail"
                    ref="email"
                    :placeholder="emailGhostText ? emailGhostText : 'Enter your email'"
                    autocomplete="off"
                    required
                    :class="[
                      {
                        error: $v.customerDetail.customerEmail.$error || !isCorrectEmailDomain || !isHubSpotEmailValid,
                      },
                      'shadow-none',
                      $v.customerDetail.customerName.$dirty &&
                      !$v.customerDetail.customerName.valid
                        ? 'mt-5'
                        : 'mt-30',
                    ]"
                    @keyup.enter="setNextInput('mobile')"
                  />
                  <p
                    v-if="
                      $v.customerDetail.customerEmail.$dirty &&
                      !$v.customerDetail.customerEmail.required
                    "
                    class="msg-error mb-0"
                  >
                    <i>Please enter your email</i>
                  </p>
                  <p
                    v-else-if="
                      $v.customerDetail.customerEmail.$dirty &&
                      !$v.customerDetail.customerEmail.email
                    "
                    class="msg-error mb-0"
                  >
                    <i>Please enter a valid email address!</i>
                  </p>
                  <p
                    v-else-if="
                      $v.customerDetail.customerEmail.$dirty &&
                      !$v.customerDetail.customerEmail.maxLength
                    "
                    class="msg-error mb-0"
                  >
                    <i>Please enter 1 to 256 characters!</i>
                  </p>
                  <p
                    v-else-if="!isCorrectEmailDomain"
                    class="msg-error mb-0"
                  >
                    <i>Invalid email address entered!</i>
                  </p>
                  <p
                    v-else-if="!isHubSpotEmailValid"
                    class="msg-error mb-0"
                  >
                    <i>Please provide a valid email address.</i>
                  </p>
                </float-label>
              </div>
            </b-col>
            <b-col cols="12" md="6">
              <div 
                class="position-relative"
                :class="(isPhoneNumberValid || !isNotShowErrMsg) && customerDetail.customerMobile.length !== 0 ? 'content-detail-mobile' : ''"
              >
                <float-label label="Mobile">
                  <b-input
                    v-model.trim="customerDetail.customerMobile"
                    ref="mobile"
                    placeholder="Enter your phone"
                    autocomplete="off"
                    required
                    pattern="\d*"
                    type="number"
                    @keypress="isNumber"
                    :class="[
                      {
                        error: $v.customerDetail.customerMobile.$error,
                      },
                      'shadow-none mt-30',
                    ]"
                    @keyup.enter="setNextInput('dealership')"
                    @focusout="handleFocusOut"
                  />
                  <p
                    v-if="
                      $v.customerDetail.customerMobile.$dirty &&
                      !$v.customerDetail.customerMobile.required
                    "
                    class="msg-error mb-0"
                  >
                    <i>Please enter your phone</i>
                  </p>
                  <p
                    v-else-if="isPhoneNumberValid || !isNotShowErrMsg"
                    class="msg-error mb-0"
                  >
                    <i>Please enter a valid mobile number starting with '04'</i>
                  </p>
                  <p
                    v-else-if="isInPhoneNumberRule || phoneNumberRuleCheck"
                    class="msg-error mb-0"
                  >
                    <i>Invalid mobile number</i>
                  </p>
                </float-label>
              </div>
            </b-col>
            <b-col cols="12" md="6">
              <div class="position-relative">
                <float-label label="Seller name">
                  <b-input
                    v-model.trim="customerDetail.company"
                    ref="dealership"
                    placeholder="Enter your business name"
                    autocomplete="off"
                    class="shadow-none mt-30"
                    @keyup="removeDuplicateSpace"
                  />
                </float-label>
              </div>
            </b-col>
            <b-col cols="12" md="6">
              <div class="position-relative">
                <google-place-autocomplete
                  label="Car pick-up location"
                  v-model="customerDetail.customerPostalCode"
                  :error="$v.customerDetail.customerPostalCode.$error"
                  name="location"
                  placeholder="Start typing your postcode"
                  :helperText="postalCodeHelperText"
                  class="mt-30 pickup-location"
                  @input="handleLocationInput"
                />
              </div>
            </b-col>
          </b-row>
        </div>
      </div>
      <btn-group
        @onOk="submitForm"
        okText="submit"
        @onCancel="goBack"
        :okBtnDisable="activeBtnAction"
        :isLoadingApi="isLoadingApi"
        :query=this.$route.query
        :customClasses="!isMobile ? 'pb-100' : ''"
        :disableNext="hasSubmitted"
      />
    </div>
    <p class="customer-detail-txt-note font-weight-thin mt-2 mb-0">
      <!-- DMV-154 -->
      <!-- Vehicle valuations are provided by gathering data from several sources.
      The valuations displayed are estimates only and are based on the vehicle
      information provided by the vehicle owner at the time of submitting this
      form, including vehicle specification, kilometer, and condition. The
      actual price of the vehicle may vary, Autoflip assumes no responsibility
      for any errors or omissions -->
    </p>
    <div :style="isMobile ? 'height: 100px;' : 'height: 70px;'"></div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  required,
  maxLength,
  email,
  helpers,
  numeric,
} from "vuelidate/lib/validators";
import mixin from "@/mixins/common";
import { SET_INFO } from "@/store/info/actions";
import customerDetailService from "@/services/customer-detail";
import { LP_TYPE } from "@/config";
import { LASTEST_DEAL } from "@/config/constants";
import { eraseCookie, getCookie } from "@/helper/cookie";
import { decrypt, b64dec, removeFormatNumberWithComma, getDecodedEncBuffer, LocationValMap, logError } from "@/helper/utils";
import debounce from "lodash/debounce";

const regexName = helpers.regex("name", /^[a-zA-Z0-9 ]+$/);
// const regexPhone = helpers.regex(
//   "number",
//   /^(\+614|04)\d{8,}$/
// );
const regexPhoneRule = helpers.regex(
  "number",
  /^04\d{8,}$/
);

export default {
  name: "CustomerDetail",
  mixins: [mixin],
  data() {
    return {
      customerDetail: { ...this.$store.getters.curVehicleInfo },
      isLoadingApi: false,
      isMobile: true,
      carSale: LASTEST_DEAL,
      from: 0,
      to: 0,
      settings: {
        dots: false,
        arrows: false,
        focusOnSelect: true,
        swipeToSlide: true,
        infinite: false,
        slidesToShow: 3,
        slidesToScroll: 1,
      },
      suburbAndPostcodes: [],
      isPhoneNumberValid: false,
      isInPhoneNumberRule: false,
      isNotShowErrMsg: false,
      isValidLocation: false,
      isCorrectEmailDomain: true,
      isNotShowFullNameErr: true,
      hasSubmitted: false,
      isHubSpotEmailValid: true,
    };
  },
  methods: {
    async submitForm() {
      this.$v.customerDetail.$touch();
      if (!this.fullNameValidCheck()) this.isNotShowFullNameErr = false;
      if (!this.phoneNumberValidCheck() || 
        (!this.$v.customerDetail.$invalid && !this.isLoadingApi && (this.customerDetail.customerMobile === '0' || this.customerDetail.customerMobile === '04'))) {
          this.isNotShowErrMsg = false;
      }
      if (
        !this.$v.customerDetail.$invalid &&
        !this.isLoadingApi &&
        this.activeBtnAction
      ) {
        this.$store.dispatch(SET_INFO, {
          ...this.customerDetail,
        });
        this.isLoadingApi = true;
        this.hasSubmitted = true;
        const transKms = removeFormatNumberWithComma(this.info.kms);
        const ipAddress = await this.getClientIP();
        customerDetailService
          .carDetail({
            ...this.customerDetail,
            model_year: this.customerDetail.model_year && this.customerDetail.model_year !== 'Not sure' && this.customerDetail.model_year.toString(),
            leadSource: LP_TYPE,
            queryString: this.queryString,
            km: transKms,
            kms: transKms,
            rbc: this.info.rbcs.length === 1 ? this.info.rbcs[0] : '',
            usedGuidedCamera: this.takePhotoWithGuidedCamera,
            images: this.$store.getters.uploadImage.filter(image => !Object.hasOwnProperty.call(image, "err")),
            ipAddress,
            openedGuidedCamera: this.openedGuidedCamera,
            searched: this.searched,
            rbLookupFlag: this.rbLookupFlag,
          })
          .then((res) => {
            if (res.status === 201 && res.data) {
              eraseCookie("qs");
              this.isLoadingApi = false;
              this.$router.push({ name: "thanks-page" });
            }
          })
          .catch((err) => {
            this.isLoadingApi = false;
            this.hasSubmitted = false;
            if (err.response && err.response.status === 422) {
              const errors = err.response.data.errors;
              Object.keys(errors).forEach(field => {
                this.$toast.clear();
                this.$toast.error(`${err.response.data.message}: ${errors[field][0]}`);
              });
            } else if (err.response) {
              if (err.response.data.message.includes('VALIDATION_ERROR')) {
                this.$toast.clear();
                this.$toast.error('Please provide a valid email address.');
                this.isHubSpotEmailValid = false;
                this.$v.customerDetail.customerEmail.$error = true;
              } else {
                this.$toast.clear();
                this.$toast.error(err.response.data.message);
              }
            } else {
              logError(`Submission error: ${err}`);
            }
          });
      }
    },
    goBack() {
      if (!this.isLoadingApi) {
        this.$store.dispatch(SET_INFO, {
            ...this.customerDetail,
          });
          this.$router.push({ name: "photo-upload", query: this.$route.query });
      }
    },
    setNextInput(val) {
      this.$refs[val].focus();
    },
    removeFirstZero() {
      if (/^0/.test(this.customerDetail.reservePrice)) {
        this.customerDetail.reservePrice =
          this.customerDetail.reservePrice.replace(/^0+/, "");
      }
    },
    getPriceRange() {
      customerDetailService
        .getPriceRange({
          make: this.info.make,
          model: this.info.model !== 'Other' ? this.info.model : '',
          year: this.info.year,
          exteriorCondition: this.customerDetail.exterior,
          rbcs: encodeURIComponent(JSON.stringify(this.info.rbcs))
        })
        .then(({ data }) => {
          this.from = data.from || 0;
          this.to = data.to || 0;
        });
    },
    isNumber: function (evt) {
      evt = evt ? evt : window.event;
      let charCode = evt.which ? evt.which : evt.keyCode;
      if (
        charCode >= 48 &&
        charCode <= 57 &&
        !(charCode === 48 && this.info.kms === "")
      ) {
        return true;
      } else {
        evt.preventDefault();
        return false;
      }
    },
    decryptBpid() {
      const queryString = getCookie("qs");
      if (queryString) {
        try {
          const buf = getDecodedEncBuffer(queryString);
          if (!buf) return;
          const parts = buf.split(':');
          const encryptedString = Buffer.from(parts[1], 'base64');
          const decrypted = decrypt(b64dec(encryptedString));
          console.log('decrypted',decrypted);
          const urlParams = new URLSearchParams(decrypted);

          this.customerDetail.company = urlParams.get('dealership')
          this.customerDetail.customerName = urlParams.get('name')
          this.customerDetail.customerEmail = urlParams.get('email')
          this.customerDetail.customerMobile = urlParams.get('mobile')
        } catch (err) {
          logError(`[Customer details page] decryptBpid error: ${err}`);
        }
      }
    },
    removeDuplicateSpace() {
      this.customerDetail.company = 
        this.customerDetail.company.replace(/\s+/g, ' ');
    },
    phoneNumberValidCheck() {
      if (this.customerDetail.customerMobile === '0' || this.customerDetail.customerMobile === '04' || 
      (this.customerDetail.customerMobile.startsWith('04') && this.customerDetail.customerMobile.length === 10)) {
        this.isNotShowErrMsg = true;
        return true;
      } else {
        this.isNotShowErrMsg = false;
        return false;
      }
    },
    handleFocusOut() {
      if (this.customerDetail.customerMobile.length > 0) {
        this.phoneNumberValidCheck();
        this.$v.customerDetail.customerMobile.$touch();
      } 
    },
    handleLocationInput: function (val) {
      if (val !== '') {
        this.isValidLocation = true;
      } else {
        this.isValidLocation = false;
      }
    },
    async getClientIP() {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        return data.ip;
      } catch (error) {
        console.error('Error fetching client IP:', error);
      }
    },
    async checkEmailWithDns(email) {
      if (!email.includes("@") || this.$v.customerDetail.customerEmail.$invalid) return;
      const domain = email.split("@")[1];
      try {
        const response = await customerDetailService.resolveDomain(domain);
        this.isCorrectEmailDomain =
          response.status === 200 && response.data ? true : false;
      } catch (error) {
        console.error("Error resolving domain:", error);
        this.isCorrectEmailDomain = false;
      }
    },
    fullNameValidCheck() {
      const words = this.customerDetail.customerName.split(/\s+/);
      if (words.length < 2 || words.some(word => word.length < 2)) {
        this.isNotShowFullNameErr = false;
        return false;
      } else {
        this.isNotShowFullNameErr = true;
        return true;
      }
    },
  },
  computed: {
    ...mapState({
      steps: (state) => state.common.steps,
      info: (state) => state.info.curVehicleInfo,
      queryString: (state) => state.common.queryString,
      takePhotoWithGuidedCamera: (state) => state.photo.takePhotoWithGuidedCamera,
      emailGhostText: (state) => state.commonVariables.copyBlocks.emailGhostText,
      openedGuidedCamera: (state) => state.photo.openedGuidedCamera,
      searched: (state) => state.regoSearch.searched,
      rbLookupFlag: (state) => state.regoSearch.rbLookupFlag,
    }),
    activeBtnAction: function () {
      return (
        !this.hasSubmitted &&
        !this.$v.customerDetail.customerName.$error &&
        !this.$v.customerDetail.customerEmail.$error &&
        !this.$v.customerDetail.customerMobile.$error &&
        !this.$v.customerDetail.customerPostalCode.$error &&
        !!this.customerDetail.customerName &&
        !!this.customerDetail.customerEmail &&
        !!this.customerDetail.customerMobile &&
        !!this.customerDetail.customerPostalCode && 
        this.isNotShowErrMsg && 
        this.customerDetail.customerMobile.startsWith('04') &&
        this.customerDetail.customerMobile.length === 10 &&
        this.isValidLocation &&
        this.isCorrectEmailDomain &&
        this.isNotShowFullNameErr
      );
    },
    fromPrice() {
      return "$" + new Intl.NumberFormat().format(this.from);
    },
    toPrice() {
      return "$" + new Intl.NumberFormat().format(this.to);
    },
    mobileMediaQuery: function() { 
      return window.matchMedia('(min-width: 768px)');
    },
    phoneNumberRuleCheck() {
      return this.$v.customerDetail.customerMobile.$dirty && !this.$v.customerDetail.customerMobile.rule;
    },
    postalCodeHelperText() {
      return this.customerDetail.customerPostalCode !== ""
        ? "Please select a valid location from the dropdown in the format </br> 'Suburb State Postcode', e.g. 'Bondi Beach NSW 2026'"
        : "Please enter a suburb/postcode";
    },
  },
  validations: {
    customerDetail: {
      reservePrice: {
        required,
        numeric,
        maxLength: maxLength(20),
      },
      customerName: {
        required,
        valid: regexName,
        maxLength: maxLength(110),
        validFullName: function() {
          return this.fullNameValidCheck()
        }
      },
      customerEmail: {
        required,
        email,
        maxLength: maxLength(256),
      },
      customerMobile: {
        required,
        valid: function() {
          return this.isNotShowErrMsg;
        },
        rule: function() {
          if (this.customerDetail.customerMobile.length === 10) {
            return regexPhoneRule(this.customerDetail.customerMobile);
          }
          return true;
        },
      },
      customerPostalCode: {
        required,
        valid: function() {
          const locationDetails = LocationValMap(this.customerDetail.customerPostalCode);
          // Regex to check for non-English characters
          const nonEnglishRegex = /[^\u0020-\u007E]/;

          // Check if customerPostalCode contains only English characters
          if (nonEnglishRegex.test(this.customerDetail.customerPostalCode)) return false;

          return locationDetails.postCode && locationDetails.state && locationDetails.suburb;
        }
      },
    },
  },
  watch: {
    'customerDetail.customerMobile'(newVal) {
      if (newVal === '' || newVal === null || newVal === undefined) {
        return;
      }
      this.$v.customerDetail.customerMobile.$touch();
      if (this.customerDetail.customerMobile.startsWith('04') && this.customerDetail.customerMobile.length < 10) {
        this.isPhoneNumberValid = false;
        this.isInPhoneNumberRule = false;
      } else if (!this.phoneNumberValidCheck()) {
        this.isPhoneNumberValid = true;
      } else if (this.phoneNumberRuleCheck) {
        this.isPhoneNumberValid = false;
        this.isInPhoneNumberRule = true;
      } else {
        this.isPhoneNumberValid = false;
        this.isInPhoneNumberRule = false;
      }
    },
    'customerDetail.customerEmail': {
      handler: debounce(function (newVal) {
        if (newVal === '' || newVal === null || newVal === undefined) {
          return;
        }
        this.checkEmailWithDns(newVal);
        this.isHubSpotEmailValid = true;
      }, 500),
      immediate: false, // The watcher will not run when the component is created
    },
  },
  mounted() {
    setTimeout(() => {
      if (this.$refs.reservePriceInput) {
        this.$nextTick(() => this.$refs.reservePriceInput.focus());
      }
    }, 1000);
    this.customerDetail = {
      ...this.customerDetail,
      kms: this.customerDetail.kms,
      keys:
        this.customerDetail.key === "2+"
          ? 2
          : parseInt(this.customerDetail.key),
      interiorCondition: this.customerDetail.interior,
      exteriorCondition: this.customerDetail.exterior,
      images: [],
    };
    if (!this.$store.getters.sendPhotoLater) {
      this.customerDetail = {
        ...this.customerDetail,
        images: this.$store.getters.uploadImage,
      };
    }
    this.decryptBpid(),
    
    this.mobileMediaQuery.addListener(e => {
      if (e.matches) {
        this.isMobile = false;
      } else {
        this.isMobile = true;
      }
    });

    this.isMobile = !this.mobileMediaQuery.matches;

    this.isNotShowErrMsg = true;

    //GoAffPro
    const inputs = document.querySelectorAll("input[placeholder='Enter your name'], input[placeholder='Enter your email'], input[placeholder='Enter your phone']");
    inputs.forEach(input => {
      if (input.placeholder === "Enter your name") {
        input.removeEventListener("change", () => {
          localStorage.setItem("nameValue", input.value);
        });
        input.addEventListener("change", () => {
          localStorage.setItem("nameValue", input.value);
        });
      } else if (input.placeholder === "Enter your email") {
        input.removeEventListener("change", () => {
          localStorage.setItem("emailValue", input.value);
        });
        input.addEventListener("change", () => {
          localStorage.setItem("emailValue", input.value);
        });
      } else if (input.placeholder === "Enter your phone") {
        input.removeEventListener("change", () => {
          localStorage.setItem("phoneValue", input.value);
        });
        input.addEventListener("change", () => {
          localStorage.setItem("phoneValue", input.value);
        });
      }
    });

    if (this.customerDetail.customerPostalCode !== '') this.isValidLocation = true;
  },
  components: {
    FloatLabel: () => import("../../components/common/FloatLabel.vue"),
    BtnGroup: () => import("../../components/common/BtnGroup.vue"),
    GooglePlaceAutocomplete: () => import("../../components/common/GooglePlaceAutocomplete.vue"),
  },
  created() {
    this.getPriceRange();
  },
};
</script>

<style lang="scss">
.msg-error {
  font-size: 12px;
  color: red;
  text-align: left;
  float: right;
  margin-top: 2px;
}
.detail-title{
  margin-left: 22px;
  margin-top: 20px;
}
.customer-detail {
  padding: 20px 15px 50px;
  background-color: #ffffff;
  border-radius: 20px;

  .customer-detail-content {
    padding: 0 8px;
    .title {
      line-height: 20px;
    }

    .left-content-detail .content-detail {
      padding: 40px 0px;
      margin-top: 10px;
    }

    .right-content-detail .content-detail {
      padding: 37px 0px 20px;
      margin-top: 15px;
    }

    .left-content-detail,
    .right-content-detail {
      // width: calc(100% / 2 - 15px);

      .content-detail {
        background-color: #f1f1f1;
        border-radius: 20px;
        padding-bottom: 20px;
        .price-desc {
          font-size: 8px;
          color: #999999;
        }

        .autoflip-price {
          color: #0ab76d;
          max-width: 350px;
          height: 50px;
          margin: 6px auto 40px;
          background-color: #ffffff;
          border-radius: 10px;
          width: 80%;
        }

        .latest-autoflip-content {
          .autoflip-slide {
            max-width: 434px;

            .slick-slide {
              display: flex;
              justify-content: center;
              align-items: center;

              div {
                max-width: 138px;
              }

              .item {
                width: fit-content;
                background-color: #ffffff;
                border-radius: 10px;

                .img {
                  width: 26vw;
                  height: calc(26vw * 0.6);
                  background-color: #dadada;
                  background-repeat: no-repeat;
                  background-position: center;
                  background-size: cover;
                  border-top-left-radius: 10px;
                  border-top-right-radius: 10px;
                }

                .autoflip-info {
                  padding: 5px 8px;

                  .price-item {
                    color: #00b86d;
                    font-size: 12px;
                    margin-top: 6px;
                  }
                }
              }
            }
          }
        }

        .error {
          border-color: red !important;
        }

        .msg-error {
          font-size: 12px;
          color: red;
          position: absolute;
          text-align: left;
          margin-top: 2px;
        }

        input {
          font-size: 16px;
          color: #383C3F;
          padding-left: 20px;
          font-weight: bold;
          border-color: #b3b3b3;
          border-radius: 10px;

          &:focus {
            box-shadow: none;
            border-color: #00bb65;
            outline: 0;
          }

          &:disabled {
            background-color: #ffffff;
            border-color: #b3b3b3;
          }
        }

        span {
          &:after {
            background: #f1f1f1;
          }
        }

        .border-lable-flt span {
          background-color: unset;
        }
      }
    }
  }

  .action {
    margin-top: 43px;

    .btn-action {
      width: 100%;
      height: 52px;
      padding: 16px 24px;
      margin-top: 12px;
      background-color: #C4C4C4;
      border-radius: 0.5rem;
      font-size: 16px;
      text-transform: uppercase;
      cursor: pointer;
    }

    .active-btn-action {
      background-color: #2BB673;

      &:hover, &:active {
        background: linear-gradient(0deg, rgba(98, 0, 238, 0.08), rgba(98, 0, 238, 0.08)), #2BB673 !important;
      }
    }

    .btn-loading {
      font-size: 16px;
      width: 24px;
      height: 24px;
    }

    .btn-back {
      background-color: #ffffff !important;
      border: 1px solid #2BB673;
      color: #2BB673;
    }

    .btn-submit {
      &:hover, &:active {
        background: linear-gradient(0deg, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.08)), #C4C4C4;
        outline: none;
      }
    }
  }

  .policy-customer {
    color: #999999;
    margin-top: 10px;

    .policy-checkbox {
      label::before,
      label::after {
        width: 20px;
        height: 20px;
      }

      label::before {
        border-radius: 5px;
      }
    }

    input:checked ~ label::before {
      color: #74bd58;
      background-color: #ffffff;
      border-color: #74bd58;
    }

    input:checked ~ label::after {
      background-image: url("~@/assets/images/icons/ic_done.svg") !important;
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
    }

    .txt-checked {
      color: #74bd58;

      a {
        color: #74bd58;
      }
    }

    span {
      margin: 3px 0 0 3px;
    }

    a {
      color: #999999;
      text-decoration: underline;
    }
  }
}

.customer-detail-txt-note {
  font-size: 11px;
  color: #989898;
  text-align: center;
  max-width: 1080px;
}
.no-price {
  height: 80px !important;
}

.pickup-location {
  #caret {
    margin: 0 15px !important;
  }
}

@media screen and(min-width: 768px) {
  .detail-title{
    margin-left: 70px !important;
  }
  .customer-detail {
    padding: 20px 51px 40px 56px;
    .customer-detail-content {
      .title {
        font-size: 20px;
        line-height: 24px;
      }
      .left-content-detail .content-detail {
        padding: 40px 5px;
        margin-top: 10px;
      }

      .right-content-detail {
        text-align: left;
        .content-detail {
          margin: 49px 0 0 !important;
          padding: 40px 26px;
  
          .price-desc {
            font-size: 12px;
          }
        }
      }
    }
    .action {
      flex-direction: row !important;
      // max-width: 600px !important;
      justify-content: space-between;
      .btn-action {
        width: 10%;
        outline: none !important;
      }
    }
  }
}
@media screen and (min-width: 768px) and (max-width: 1200px) {
  .content-detail-mobile {
    margin-bottom: 25px !important;
  }
}
@media screen and (max-width: 460px) {
  .content-detail-mobile {
    margin-bottom: 25px !important;
  }
}
</style>
